import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Image, Upload, Trash2, Plus, Loader2, Check, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MULTI_IMAGE_SLOTS = ['homepage_hero'];

const DEFAULT_SLOTS = [
  { key: 'homepage_hero', label: 'Homepage Hero Images', description: 'Hero slideshow images (auto-cycles every 5s with crossfade)' },
  { key: 'about_hero', label: 'About Page Hero', description: 'Hero image on the About page' },
  { key: 'contact_hero', label: 'Contact Page Hero', description: 'Hero image on the Contact page' },
  { key: 'trade_hero', label: 'Trade Application Hero', description: 'Hero image on the Trade Application page' },
];

const resolveUrl = (url) =>
  url && url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

function SortableHeroImage({ url, idx, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: url });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="relative group aspect-video bg-zinc-800 rounded-lg overflow-hidden">
      <img src={resolveUrl(url)} alt={`Slide ${idx + 1}`} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
        <button
          {...attributes}
          {...listeners}
          className="p-1.5 bg-zinc-700 rounded cursor-grab active:cursor-grabbing"
          data-testid={`drag-hero-${idx}`}
        >
          <GripVertical className="h-4 w-4 text-white" />
        </button>
        <Button size="sm" variant="destructive" className="h-7 px-2" onClick={() => onRemove(idx)} data-testid={`remove-hero-${idx}`}>
          <Trash2 className="h-3 w-3 mr-1" /> Remove
        </Button>
      </div>
      <div className="absolute top-1.5 left-1.5 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded font-mono">
        {idx + 1}
      </div>
    </div>
  );
}

function MultiImageSlot({ slot, image, onSave }) {
  const [uploading, setUploading] = useState(false);
  const urls = image?.urls?.length ? image.urls : (image?.url ? [image.url] : []);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = urls.indexOf(active.id);
    const newIndex = urls.indexOf(over.id);
    const reordered = arrayMove(urls, oldIndex, newIndex);
    await onSave(slot.key, reordered, slot.label);
    toast.success('Order updated');
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setUploading(true);
    const newUrls = [...urls];
    for (const file of acceptedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/upload/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        newUrls.push(res.data.url);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    await onSave(slot.key, newUrls, slot.label);
    toast.success('Images updated');
    setUploading(false);
  }, [slot, urls, onSave]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'] },
    maxSize: 10 * 1024 * 1024,
    multiple: true,
    disabled: uploading,
  });

  const removeImage = async (index) => {
    const newUrls = urls.filter((_, i) => i !== index);
    await onSave(slot.key, newUrls, slot.label);
    toast.success('Image removed');
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden col-span-2" data-testid={`site-image-${slot.key}`}>
      <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
        <div>
          <h3 className="text-white font-medium text-sm">{slot.label}</h3>
          <p className="text-zinc-500 text-xs mt-0.5">{slot.description}</p>
        </div>
        {urls.length > 0 && (
          <span className="text-xs bg-[#78BE20]/20 text-[#78BE20] px-2 py-0.5 rounded">
            {urls.length} image{urls.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="p-4">
        {/* Existing images grid - drag to reorder */}
        {urls.length > 0 && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={urls} strategy={horizontalListSortingStrategy}>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
                {urls.map((url, idx) => (
                  <SortableHeroImage key={url} url={url} idx={idx} onRemove={removeImage} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {/* Drop zone for adding more */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg h-32 flex flex-col items-center justify-center cursor-pointer transition-colors ${
            isDragActive ? 'border-[#78BE20] bg-[#78BE20]/10' : 'border-zinc-700 hover:border-zinc-500'
          } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <>
              <Loader2 className="h-6 w-6 text-[#78BE20] animate-spin mb-1" />
              <p className="text-zinc-400 text-sm">Uploading...</p>
            </>
          ) : isDragActive ? (
            <>
              <Upload className="h-6 w-6 text-[#78BE20] mb-1" />
              <p className="text-[#78BE20] text-sm">Drop images here</p>
            </>
          ) : (
            <>
              <Plus className="h-6 w-6 text-zinc-500 mb-1" />
              <p className="text-zinc-400 text-sm">{urls.length > 0 ? 'Add more images' : 'Drag & drop images'}</p>
              <p className="text-zinc-600 text-xs">JPG, PNG, WebP up to 10MB each</p>
            </>
          )}
        </div>
      </div>
      {image?.updated_at && (
        <div className="px-4 pb-3">
          <p className="text-zinc-600 text-xs">
            Updated {new Date(image.updated_at).toLocaleDateString('en-AU')} by {image.updated_by || 'admin'}
          </p>
        </div>
      )}
    </div>
  );
}

function ImageSlot({ slot, image, onUpload, onRemove }) {
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', acceptedFiles[0]);
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await onUpload(slot.key, res.data.url, slot.label);
      toast.success(`${slot.label} updated`);
    } catch (err) {
      toast.error(`Failed to upload: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploading(false);
    }
  }, [slot, onUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'] },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
    disabled: uploading,
  });

  const imageUrl = image?.url ? resolveUrl(image.url) : null;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden" data-testid={`site-image-${slot.key}`}>
      <div className="p-4 border-b border-zinc-800">
        <h3 className="text-white font-medium text-sm">{slot.label}</h3>
        <p className="text-zinc-500 text-xs mt-0.5">{slot.description}</p>
      </div>
      <div className="p-4">
        {imageUrl ? (
          <div className="relative group">
            <img src={imageUrl} alt={slot.label} className="w-full h-48 object-cover rounded-lg" />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-3">
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <Button size="sm" className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid={`replace-${slot.key}`}>
                  <Upload className="h-4 w-4 mr-1" /> Replace
                </Button>
              </div>
              <Button size="sm" variant="destructive" onClick={() => onRemove(slot.key)} data-testid={`remove-${slot.key}`}>
                <Trash2 className="h-4 w-4 mr-1" /> Remove
              </Button>
            </div>
            <div className="absolute bottom-2 right-2 bg-[#78BE20]/90 text-black text-xs px-2 py-0.5 rounded flex items-center gap-1">
              <Check className="h-3 w-3" /> Uploaded
            </div>
          </div>
        ) : (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg h-48 flex flex-col items-center justify-center cursor-pointer transition-colors ${
              isDragActive ? 'border-[#78BE20] bg-[#78BE20]/10' : 'border-zinc-700 hover:border-zinc-500'
            } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <input {...getInputProps()} />
            {uploading ? (
              <>
                <Loader2 className="h-8 w-8 text-[#78BE20] animate-spin mb-2" />
                <p className="text-zinc-400 text-sm">Uploading...</p>
              </>
            ) : isDragActive ? (
              <>
                <Upload className="h-8 w-8 text-[#78BE20] mb-2" />
                <p className="text-[#78BE20] text-sm">Drop image here</p>
              </>
            ) : (
              <>
                <Image className="h-8 w-8 text-zinc-600 mb-2" />
                <p className="text-zinc-400 text-sm">Drag & drop an image</p>
                <p className="text-zinc-600 text-xs mt-1">or click to browse</p>
              </>
            )}
          </div>
        )}
      </div>
      {image?.updated_at && (
        <div className="px-4 pb-3">
          <p className="text-zinc-600 text-xs">
            Updated {new Date(image.updated_at).toLocaleDateString('en-AU')} by {image.updated_by || 'admin'}
          </p>
        </div>
      )}
    </div>
  );
}

export default function AdminSiteImages() {
  const [images, setImages] = useState([]);
  const [customSlots, setCustomSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newSlot, setNewSlot] = useState({ key: '', label: '', description: '' });

  useEffect(() => { fetchImages(); }, []);

  const fetchImages = async () => {
    try {
      const res = await axios.get(`${API}/admin/site-images`);
      setImages(res.data);
      const defaultKeys = DEFAULT_SLOTS.map(s => s.key);
      const custom = res.data
        .filter(img => !defaultKeys.includes(img.key))
        .map(img => ({ key: img.key, label: img.label || img.key, description: 'Custom image slot' }));
      setCustomSlots(custom);
    } catch (err) {
      console.error('Error fetching site images:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (key, url, label) => {
    await axios.put(`${API}/admin/site-images/${key}`, { url, label });
    fetchImages();
  };

  const handleMultiSave = async (key, urls, label) => {
    await axios.put(`${API}/admin/site-images/${key}`, { url: urls[0] || '', urls, label });
    fetchImages();
  };

  const handleRemove = async (key) => {
    if (!window.confirm('Remove this image?')) return;
    await axios.delete(`${API}/admin/site-images/${key}`);
    toast.success('Image removed');
    fetchImages();
  };

  const handleAddCustomSlot = async () => {
    if (!newSlot.key.trim() || !newSlot.label.trim()) {
      toast.error('Key and label are required');
      return;
    }
    const key = newSlot.key.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    setCustomSlots([...customSlots, { key, label: newSlot.label, description: newSlot.description || 'Custom image' }]);
    setShowAddDialog(false);
    setNewSlot({ key: '', label: '', description: '' });
  };

  const getImageForSlot = (key) => images.find(img => img.key === key);
  const allSlots = [...DEFAULT_SLOTS, ...customSlots];

  return (
    <AdminLayout>
      <div data-testid="admin-site-images">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-black text-white uppercase tracking-tight">Site Images</h1>
            <p className="text-zinc-400 mt-1 text-sm">Upload and manage hero images and banners across the site</p>
          </div>
          <Button onClick={() => setShowAddDialog(true)} className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="add-slot-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Image Slot
          </Button>
        </div>

        {loading ? (
          <div className="text-zinc-400">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {allSlots.map(slot =>
              MULTI_IMAGE_SLOTS.includes(slot.key) ? (
                <MultiImageSlot
                  key={slot.key}
                  slot={slot}
                  image={getImageForSlot(slot.key)}
                  onSave={handleMultiSave}
                />
              ) : (
                <ImageSlot
                  key={slot.key}
                  slot={slot}
                  image={getImageForSlot(slot.key)}
                  onUpload={handleUpload}
                  onRemove={handleRemove}
                />
              )
            )}
          </div>
        )}

        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Add Custom Image Slot</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Label</Label>
                <Input
                  value={newSlot.label}
                  onChange={(e) => setNewSlot({
                    ...newSlot,
                    label: e.target.value,
                    key: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
                  })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  placeholder="e.g., Products Banner"
                  data-testid="new-slot-label"
                />
              </div>
              <div>
                <Label>Key (auto-generated)</Label>
                <Input value={newSlot.key} readOnly className="mt-1 bg-zinc-800 border-zinc-700 text-zinc-500" />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={newSlot.description}
                  onChange={(e) => setNewSlot({ ...newSlot, description: e.target.value })}
                  className="mt-1 bg-zinc-800 border-zinc-700"
                  placeholder="Where this image is used"
                  data-testid="new-slot-description"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button onClick={handleAddCustomSlot} className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="confirm-add-slot">
                  Add Slot
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
